import {useContext, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Box, Card, CardContent, Typography, Button, Chip, CircularProgress, Divider, Stack, Tooltip, IconButton} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {STATUS_COLORS} from "../../../config/statusColors";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Clock01Icon, DatabaseIcon, File02Icon, Message01Icon, ServerIcon, Settings01Icon, Copy01Icon, CheckIcon} from "@hugeicons/core-free-icons";
import {Context} from "../../../context/AppContext";
import {KnowledgeManagerLayout} from "./components/KnowledgeManagerLayout";
import {CreateTopicModal} from "./components/CreateTopicModal";
import {CollectionViewToggle} from "../../../components/CollectionViewToggle";
import {useCollectionView} from "../../../hooks/useCollectionView";
import {KnowledgeTopicsList} from "./components/KnowledgeTopicsList";
import {collectionCardSx, collectionCardContentSx, collectionCardTitleSx, collectionCardDescriptionSx, collectionCardBadgeSx} from "../../../components/collectionCardStyles";
import {StatCard} from "../../../components/StatCard";
import {copyToClipboard} from "../../../utils/functions";

const formatShortDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return d.toLocaleDateString("en-US", {month: "short", day: "numeric"});
};

const serverStatusColor = (status) => {
    if (status === "online") return {bg: STATUS_COLORS.success.bg, color: STATUS_COLORS.success.text};
    if (status === "connecting") return {bg: STATUS_COLORS.warning.bg, color: STATUS_COLORS.warning.text};
    return {bg: STATUS_COLORS.error.bg, color: STATUS_COLORS.error.text};
};

const topicServerUrl = (server) => {
    if (!server) return "";
    const configuredUrl = server.apiBase || server.url || server.endpoint;
    if (configuredUrl) {
        return /^https?:\/\//i.test(configuredUrl) ? configuredUrl : `http://${configuredUrl}`;
    }
    return server.host ? `http://${server.host}:${server.port}` : "";
};
export const KnowledgeManagerPage = ({project}) => {
    const [viewMode, setViewMode] = useCollectionView("knowledge-manager-view-mode", "cards");
    const {projectId} = useParams();
    const navigate = useNavigate();
    const {state, getAllKbTopics} = useContext(Context);
    const {kbTopics, kbTopicsLoading} = state;
    const [createOpen, setCreateOpen] = useState(false);
    const [copiedId, setCopiedId] = useState(null);

    const totalDocs = kbTopics.reduce((acc, t) => acc + (t.documentCount || 0), 0);
    const totalQueries = kbTopics.reduce((acc, t) => acc + (t.queryCount || 0), 0);
    const onlineServers = kbTopics.filter(t => t.server?.status === "online").length;


    const copyHandler = async (topic) => {
        try {
            const endpoint = topicServerUrl(topic.server);
            if (!endpoint) return;

            await copyToClipboard(endpoint);

            setCopiedId(topic.id);
            setTimeout(() => setCopiedId(null), 1500);
        } catch (e) {
            console.error(e);
        }
    };

    if (kbTopicsLoading && kbTopics.length === 0) {
        return (
            <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300}}>
                <CircularProgress size={28}/>
            </Box>
        );
    }

    return (
        <KnowledgeManagerLayout>
            <Box sx={{flex: 1, overflow: "auto"}}>
                <Box sx={{p: 2, width: {xs: "100%", md: "90%"}, maxWidth: "xl", mx: "auto", boxSizing: "border-box"}}>


                    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} flexWrap="wrap" gap={2} sx={{mb: 3}}>
                        <Box>
                            <Typography variant={"h5"} fontWeight={700}>Knowledge Base Dashboard</Typography>
                            <Typography variant={"body2"} color={"text.secondary"} sx={{mt: 0.5}}>
                                Manage your VoiceBot & ChatBot knowledge bases powered by LightRAG
                            </Typography>
                        </Box>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                            <Button
                                variant="ghost" size="small"
                                startIcon={<HugeiconsIcon icon={Settings01Icon} size={16}/>}
                                onClick={() => navigate(`/project/${projectId}/knowledge-manager/general-settings`)}
                            >
                                Global Settings
                            </Button>
                            <Divider orientation="vertical" variant="middle" flexItem/>
                            <Button
                                variant={"modal"}
                                startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
                                onClick={() => setCreateOpen(true)}
                            >
                                New Topic
                            </Button>
                        </Stack>
                    </Stack>


                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr"},
                        gap: 2,
                        mb: 4,
                    }}>
                        <StatCard label={"Topics"} value={kbTopics.length}
                                  icon={<HugeiconsIcon icon={DatabaseIcon} size={20} color={"#4f46e5"}/>} bg={"#eef2ff"}/>
                        <StatCard label={"Documents"} value={totalDocs}
                                  icon={<HugeiconsIcon icon={File02Icon} size={20} color={"#059669"}/>} bg={"#ecfdf5"}/>
                        <StatCard label={"Total Queries"} value={totalQueries}
                                  icon={<HugeiconsIcon icon={Message01Icon} size={20} color={"#d97706"}/>} bg={"#fffbeb"}/>
                        <StatCard
                            label={"Servers Online"}
                            value={<>{onlineServers}<Typography component={"span"} fontSize={14}
                                                                color={"text.secondary"}>/{kbTopics.length}</Typography></>}
                            icon={<HugeiconsIcon icon={ServerIcon} size={20} color={"#0284c7"}/>}
                            bg={"#f0f9ff"}
                        />
                    </Box>


                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 2}}>
                        <Typography variant={"h6"} fontWeight={700}>Topics</Typography>
                        <CollectionViewToggle value={viewMode} onChange={setViewMode}/>
                    </Stack>
                    {viewMode === "list" ? (
                        <KnowledgeTopicsList topics={kbTopics} formatDate={formatShortDate} statusColor={serverStatusColor}
                                             onCopy={copyHandler} copiedId={copiedId}
                                             onOpen={topic => navigate(`/project/${projectId}/knowledge-manager/${topic.id}`)}/>
                    ) : (
                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr"},
                        gap: 2,
                    }}>
                        {kbTopics.map(topic => {
                            return (
                                <Card
                                    key={topic.id}
                                    sx={{
                                        ...collectionCardSx,
                                        cursor: "pointer",
                                        display: "flex", minWidth: 0,
                                        borderLeft: `2px solid ${alpha(topic.color || "#60a5fa", 0.55)}`,
                                        "&:hover": {transform: "scale(1.01)"},
                                    }}
                                    onClick={() => navigate(`/project/${projectId}/knowledge-manager/${topic.id}`)}
                                >
                                    <CardContent sx={collectionCardContentSx}>
                                        <Box>
                                        <Stack direction={"row"} alignItems={"flex-start"}
                                               justifyContent={"space-between"} gap={1}>
                                            <Stack direction={"row"} alignItems={"center"} gap={1} sx={{minWidth: 0}}>
                                                <Button sx={collectionCardTitleSx} onClick={event => {
                                                    event.stopPropagation();
                                                    navigate(`/project/${projectId}/knowledge-manager/${topic.id}`);
                                                }}>{topic.name}</Button>
                                            </Stack>
                                            {topic.server && (
                                                <Tooltip title={topic.server.status}>
                                                    <Box
                                                        role="img"
                                                        aria-label={topic.server.status}
                                                        sx={{
                                                            width: 8, height: 8, borderRadius: "50%", flexShrink: 0, mt: 1,
                                                            bgcolor: topic.server.status === "online" ? "success.main"
                                                                : topic.server.status === "connecting" ? "warning.main" : "error.main",
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Stack>

                                        {topic.description && <Typography sx={collectionCardDescriptionSx}>
                                            {topic.description}
                                        </Typography>}
                                        </Box>

                                        {topic.tags?.length > 0 && <Stack direction={"row"} flexWrap={"wrap"} gap={0.75}>
                                            {topic.tags?.map(tag => (
                                                <Chip key={tag} label={tag} variant="filled" size={"small"}
                                                      sx={collectionCardBadgeSx}/>
                                            ))}
                                        </Stack>}

                                        {topic.server && (
                                            <Stack direction={"row"} alignItems={"center"} gap={1}
                                                   sx={{px: 1.25, py: 0.75, borderRadius: 2, bgcolor: "grey.50", color: "text.secondary"}}>
                                                <LinkOutlinedIcon sx={{fontSize: 18, color: "text.secondary", flexShrink: 0}}/>
                                                <Typography fontSize={12} fontFamily="monospace" color={"text.secondary"} sx={{minWidth: 0, flex: 1, overflowWrap: "anywhere"}}>
                                                    {topic.server.host}:{topic.server.port}
                                                </Typography>
                                                <Tooltip title="Copy endpoint">
                                                    <IconButton
                                                        size="small"
                                                        className="copy-btn"
                                                        sx={{opacity: copiedId === topic.id ? 1 : 0.65, p: 0.25,
                                                            "&:hover": {opacity: 1}}}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copyHandler(topic);
                                                        }}
                                                    >
                                                        {copiedId === topic.id ?
                                                            <HugeiconsIcon icon={CheckIcon} size={13} color="green"/> :
                                                            <HugeiconsIcon icon={Copy01Icon} size={13}/>}
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        )}
                                        <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: "auto"}}>
                                            {[
                                                {label: "Documents", value: topic.documentCount || 0, icon: File02Icon, color: "#64748b", bg: "#f8fafc"},
                                                {label: "Queries", value: topic.queryCount || 0, icon: Message01Icon, color: "#64748b", bg: "#f8fafc"},
                                            ].map(metric => (
                                                <Stack key={metric.label} direction="row" alignItems="center" gap={1} sx={{p: 1, borderRadius: 2, bgcolor: metric.bg}}>
                                                    <Box sx={{width: 28, height: 28, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.75)"}}>
                                                        <HugeiconsIcon icon={metric.icon} size={15} color={metric.color}/>
                                                    </Box>
                                                    <Box sx={{minWidth: 0}}>
                                                        <Typography fontSize={18} fontWeight={700} sx={{lineHeight: 1.1, color: "#475569"}}>{metric.value}</Typography>
                                                        <Typography fontSize={9} color="text.secondary" sx={{textTransform: "uppercase", letterSpacing: 0.5}}>{metric.label}</Typography>
                                                    </Box>
                                                </Stack>
                                            ))}
                                        </Box>
                                        <Stack direction="row" alignItems="center" gap={0.5} sx={{pt: 1.5, borderTop: "1px solid", borderColor: "grey.100", color: "text.secondary"}}>
                                            <HugeiconsIcon icon={Clock01Icon} size={12}/>
                                            <Typography variant="caption">Updated {formatShortDate(topic.updatedAt) || "Not set"}</Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}


                        <Card
                            sx={{
                                ...collectionCardSx,
                                cursor: "pointer",
                                p: 2.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 200,
                            }}
                            onClick={() => setCreateOpen(true)}
                        >
                            <Box sx={{textAlign: "center", color: "text.secondary"}}>
                                <HugeiconsIcon icon={Add01Icon} size={32} style={{opacity: 0.4, marginBottom: 8}}/>
                                <Typography fontSize={13}>Create New Topic</Typography>
                                <Typography fontSize={11} sx={{opacity: 0.6, mt: 0.5}}>Add a new LightRAG
                                    workspace</Typography>
                            </Box>
                        </Card>
                    </Box>

                    )}

                    {viewMode === "cards" && kbTopics.length === 0 && (
                        <Typography color={"text.secondary"} sx={{mt: 2}}>No topics created yet.</Typography>
                    )}
                </Box>
            </Box>
            <CreateTopicModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => getAllKbTopics(project?.settings?.organization)}
                project={project}
            />
        </KnowledgeManagerLayout>
    );

};
